import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

export interface PromoCode {
    id: number;
    code: string;
    description?: string;
    durationDays?: number | null;
    maxRedemptions?: number | null;
    redeemedCount: number;
    validFrom?: string | null;
    validUntil?: string | null;
    active: boolean;
    createdAt: string;
}

export interface CreatePromoCode {
    code?: string;
    description?: string;
    durationDays?: number | null;
    maxRedemptions?: number | null;
    validFrom?: string | null;
    validUntil?: string | null;
}

export interface PromoRedemption {
    id: number;
    userId: number;
    email: string;
    nickname: string;
    redeemedAt: string;
    entitlementId: number;
    entitlementStatus: string;
    expiresAt?: string | null;
}

export interface Entitlement {
    id: number;
    userId: number;
    source: string;
    productId?: string;
    store?: string;
    startsAt: string;
    expiresAt?: string | null;
    status: string;
    environment: string;
    note?: string;
    createdAt: string;
}

export interface UserHit {
    id: number;
    email: string;
    nickname: string;
}

export interface WebhookEvent {
    id: number;
    eventId: string;
    type: string;
    appUserId: string;
    receivedAt: string;
    processedAt?: string | null;
    result: string;
    error?: string;
}

@Injectable({
    providedIn: 'root'
})
export class PremiumAdminService {
    private base = `${environment.apiUrl}/admin/premium`;

    constructor(private http: HttpClient) {}

    listPromoCodes(): Observable<PromoCode[]> {
        return this.http.get<PromoCode[]>(`${this.base}/promo-codes`);
    }
    createPromoCode(dto: CreatePromoCode): Observable<PromoCode> {
        return this.http.post<PromoCode>(`${this.base}/promo-codes`, dto);
    }
    setPromoActive(id: number, active: boolean): Observable<void> {
        return this.http.put<void>(`${this.base}/promo-codes/${id}/active`, { active });
    }
    listRedemptions(id: number): Observable<PromoRedemption[]> {
        return this.http.get<PromoRedemption[]>(`${this.base}/promo-codes/${id}/redemptions`);
    }
    revokeEntitlement(id: number): Observable<void> {
        return this.http.post<void>(`${this.base}/entitlements/${id}/revoke`, {});
    }
    searchUsers(email: string): Observable<UserHit[]> {
        return this.http.get<UserHit[]>(`${this.base}/users/search`, { params: new HttpParams().set('email', email) });
    }
    userEntitlements(userId: number): Observable<Entitlement[]> {
        return this.http.get<Entitlement[]>(`${this.base}/users/${userId}/entitlements`);
    }
    grantManual(userId: number, durationDays: number | null, note: string): Observable<Entitlement> {
        return this.http.post<Entitlement>(`${this.base}/users/${userId}/entitlements/manual`, { durationDays, note });
    }
    listWebhookEvents(result: string, page: number, pageSize: number): Observable<WebhookEvent[]> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        if (result) params = params.set('result', result);
        return this.http.get<WebhookEvent[]>(`${this.base}/webhook-events`, { params });
    }
    reprocessEvent(id: number): Observable<{ outcome: string }> {
        return this.http.post<{ outcome: string }>(`${this.base}/webhook-events/${id}/reprocess`, {});
    }
}
